/**
 * Monatsabschluss — S2, die Seite zum Stand des Monatsabschlusses.
 *
 * Page-scoped (kein geteiltes Bauteil → JSDoc-Kopf, kein Registry-Eintrag,
 * Regel 12). Eine LESEfläche: kein Knopf löst etwas aus.
 *
 *   GET /api/tax/monatsabschluss?monat=YYYY-MM   (Blueprint `monatsabschluss_ansicht_bp`)
 *
 * Plan: `docs/superpowers/plans/2026-10-06-s2-monatsabschluss-seite.md` (Fassung 2)
 * und die Grill-Wiedervorlage (N1–N5). Owner-Entscheid E2: eigene Seite, 12 Gruppen
 * wie in BB, Verlauf je Monat.
 *
 * 🔴 WAS HIER NICHT VERHANDELBAR IST:
 *
 * 1. Status und Zählung kommen UNVERÄNDERT aus der Laufzeile (`cockpit`, `zaehlung`).
 *    Die Seite rechnet keinen Status nach; Titel und Gruppen kommen aus `anzeige`
 *    (Register), nie als Literal hier (Grill B2/W1).
 * 2. Ein Lauf prüft ZWEI Monate. Die Seite schreibt deshalb nie „as of August" allein,
 *    sondern die Fensterzeile mit dem Hinweis, dass die Prüfungen die Monate nicht
 *    trennen (Plan §4.2).
 * 3. „check complete as of <Stichtag>" statt „final", mit der Grenze daneben:
 *    spätere Änderungen in BB sind nicht erfasst (Grill N3).
 * 4. Die Lage der JÜNGSTEN Zeile (läuft / ohne Ergebnis) steht getrennt vom gezeigten
 *    Stand und wird auch über einem älteren Stand laut gemeldet (Grill N1, W2);
 *    „veraltet" gilt für jeden noch nicht abschliessend geprüften Monat (N2).
 * 5. Unbekannt ist nie 0 (Regel 22 Nr. 3): ohne Daten steht `—`, ein Fehlabruf hat
 *    eine Wiederholung, und „kein Ergebnis für diesen Monat" nennt seinen Grund.
 * 6. „Further checks" (Prüfungen ohne Cockpit-Zeile, heute MA7–MA9) kommen aus der
 *    Nutzlast (`weitere_pruefungen`, datengetrieben, Grill B3/N5) und heben den Kopf
 *    genauso wie eine Zeile in den 12 Gruppen — der Kopf liest `zaehlung`, die sie mitzählt.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useSearchParams } from 'react-router-dom'
import {
  Alert, Box, Button, ButtonBase, Collapse, MenuItem, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import Pill from '../components/common/Pill'
import EmptyState from '../components/common/EmptyState'
import ViewHeader from '../components/ViewHeader'
import { FS, PANEL_RADIUS, SP, CONTROL_HEIGHT_SX } from '../theme/tokens'
import {
  KEIN_WERT, KEIN_ERGEBNIS_TEXT, ZAEHLUNG_FELDER, cockpitDetail, cockpitPill, datumDe,
  fensterzeile, gruppenstand, gruppieren, kopfPill, monatsname, pruefStatusPill, zaehlwert,
  zeitpunktDe,
} from './monatsabschlussText'

const API = process.env.REACT_APP_API_BASE_URL

/** Der laufende Monat als `YYYY-MM` (UTC, wie der Endpunkt ohne `monat`). */
function laufenderMonat() {
  return new Date().toISOString().slice(0, 7)
}

function StatusPill({ pill, testid }) {
  return (
    <Pill tone={pill.tone} count={pill.count} bordered data-testid={testid}>
      {pill.text}
    </Pill>
  )
}

/** Eine Zeile der Liste: Nummer · Aufgabentitel · Pill, darunter der Grund. Schmal bricht sie um. */
function CockpitZeile({ nummer, titel, zeile, pruefungen }) {
  const pill = cockpitPill(zeile)
  const detail = cockpitDetail(zeile, pruefungen)
  return (
    <Box
      data-testid={`ma-zeile-${nummer}`}
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: SP.sm, rowGap: SP.chip,
        py: SP.tight, borderTop: '1px solid', borderColor: 'divider',
      }}
    >
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: '3em' }}>
        {nummer}
      </Typography>
      <Typography variant="body2" sx={{ flex: '1 1 16em', minWidth: 0 }}>
        {titel || 'Unknown title — the number is not in today’s register'}
      </Typography>
      <StatusPill pill={pill} testid={`ma-pill-${nummer}`} />
      {detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', pl: { sm: SP.lg } }}>
          {detail}
        </Typography>
      ) : null}
    </Box>
  )
}

/** Eine der 12 Gruppen, einklappbar. Mit Befund oder Ausfall steht sie standardmässig offen. */
function Gruppe({ gruppe, pruefungen, offen, umschalten }) {
  const stand = gruppenstand(gruppe)
  const id = `ma-gruppe-inhalt-${gruppe.nummer}`
  return (
    <Panel pad="none" clip data-testid={`ma-gruppe-${gruppe.nummer}`}>
      {/* Regel-16 W2, WAI-Akkordeon: die Überschrift liegt AUSSEN, der Knopf darin.
          Eine Überschrift im <button> verschwindet aus der Überschriften-Navigation. */}
      <Typography variant="subtitle2" component="h3" sx={{ m: 0 }}>
        <ButtonBase
          onClick={umschalten}
          aria-expanded={offen}
          aria-controls={id}
          sx={{
            width: '100%', minHeight: 48, px: SP.card, py: SP.tight, justifyContent: 'flex-start',
            textAlign: 'left', display: 'flex', flexWrap: 'wrap', columnGap: SP.sm, rowGap: SP.chip,
            borderRadius: PANEL_RADIUS, font: 'inherit',
          }}
        >
          <Box component="span" sx={{ flex: '1 1 14em' }}>
            {gruppe.nummer} · {gruppe.titel}
          </Box>
          {stand.gescheitert > 0 ? (
            <Pill tone="err" bordered count={stand.gescheitert}>could not check</Pill>
          ) : null}
          {stand.befund > 0 ? <Pill tone="warn" bordered count={stand.befund}>Finding</Pill> : null}
          <Typography component="span" variant="caption" color="text.secondary">
            {stand.geprueft} of {stand.gesamt} checked automatically
          </Typography>
        </ButtonBase>
      </Typography>
      <Collapse in={offen} id={id}>
        <Box sx={{ px: SP.card, pb: SP.tight }}>
          {gruppe.zeilen.map((z) => (
            <CockpitZeile key={z.nummer} {...z} pruefungen={pruefungen} />
          ))}
        </Box>
      </Collapse>
    </Panel>
  )
}

/** Prüfungen ohne Cockpit-Zeile (heute MA7–MA9), datengetrieben aus der Nutzlast. */
function WeiterePruefungen({ kennungen, pruefungen }) {
  if (!kennungen.length) return null
  return (
    <Panel component="section" aria-labelledby="ma-weitere-titel" data-testid="ma-weitere">
      <Typography id="ma-weitere-titel" variant="subtitle1" component="h2" sx={{ mb: SP.tight }}>
        Further checks
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: SP.tight }}>
        Checks from this run that have no task of their own in the BB cockpit.
      </Typography>
      {kennungen.map((k) => {
        const p = pruefungen[k] || {}
        const pill = pruefStatusPill(p.status, p.grund_code)
        // Regel-15 W1: nur der übersetzte Grund, nie der rohe `grund` (`str(ex)`).
        const detail = p.status === 'befund' ? p.schlagzeile : (p.grund_anzeige || '')
        return (
          <Box
            key={k}
            data-testid={`ma-weitere-${k}`}
            sx={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: SP.sm,
              rowGap: SP.chip, py: SP.tight, borderTop: '1px solid', borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ minWidth: '3em' }}>{k}</Typography>
            <Typography variant="body2" sx={{ flex: '1 1 16em', minWidth: 0 }}>{p.prueft || KEIN_WERT}</Typography>
            <StatusPill pill={pill} testid={`ma-weitere-pill-${k}`} />
            {detail ? (
              <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', pl: { sm: SP.lg } }}>
                {detail}
              </Typography>
            ) : null}
          </Box>
        )
      })}
    </Panel>
  )
}

/** Hinweise zur jüngsten Zeile und zum Alter des Stands — getrennt vom gezeigten Stand (N1). */
function Hinweise({ daten }) {
  const lage = daten.juengste_lage || {}
  const j = daten.juengste
  const aus = []
  if (lage.laeuft && j) {
    aus.push(
      <Alert key="laeuft" severity="info" data-testid="ma-hinweis-laeuft">
        A run is in progress (since {zeitpunktDe(j.started_at)})
        {daten.zeile
          ? '; showing the last finished run with a result.'
          : '; its result is not in yet.'}
      </Alert>,
    )
  } else if (lage.ohne_ergebnis && j) {
    aus.push(
      <Alert key="absturz" severity="error" data-testid="ma-hinweis-absturz">
        The most recent run ({zeitpunktDe(j.started_at)}) ended without a result: {j.abbruch || KEIN_WERT}
        {daten.zeile ? ' An older state is shown.' : ''}
      </Alert>,
    )
  }
  // 🔴 G13b-1 (§2.4 S2): OHNE Ergebniszeile sagt der Leerzustand dieselbe LAGE
  // bereits — er nennt den Grund, warum kein Ergebnis vorliegt (KEIN_ERGEBNIS_TEXT,
  // `monatsabschlussText.js`), aber ohne die Altersschwelle dieses Hinweises.
  // Bei VORHANDENER Zeile ist dieser Hinweis die einzige Auskunft über das Alter
  // und bleibt stehen.
  if (daten.veraltet && daten.zeile) {
    aus.push(
      <Alert key="veraltet" severity="warning" data-testid="ma-hinweis-veraltet">
        The state shown is older than {daten.schwelle_stunden} hours — no run has delivered a
        result since. This month has not been conclusively checked yet.
      </Alert>,
    )
  }
  return aus.length ? <Stack spacing={SP.tight}>{aus}</Stack> : null
}

function Kopf({ daten }) {
  const kopf = kopfPill(daten.zaehlung)
  const z = daten.zaehlung
  return (
    <Panel component="section" aria-label="State of the month" data-testid="ma-kopf">
      <Stack spacing={SP.tight}>
        <Typography variant="body2" data-testid="ma-fenster">{fensterzeile(daten)}</Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={SP.tight} alignItems="center">
          <StatusPill pill={kopf} testid="ma-kopf-pill" />
          {daten.abschliessend_geprueft ? (
            <Pill tone="info" bordered data-testid="ma-abschluss">
              check complete as of {datumDe(daten.abschliessend_geprueft_am)}
            </Pill>
          ) : (
            <Pill tone="plain" bordered data-testid="ma-abschluss">provisional — still being checked</Pill>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {daten.abschliessend_geprueft
            ? 'No later run checks this month. Later changes in BB are not captured.'
            : 'The daily run checks this month until the end of the following month.'}
        </Typography>
        <Box
          component="dl"
          sx={{
            m: 0, display: 'grid', gap: SP.tight,
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          {ZAEHLUNG_FELDER.map(([feld, label]) => (
            <Box key={feld}>
              <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
              <Typography
                component="dd"
                variant="body2"
                sx={{ m: 0, fontSize: FS.lg, fontVariantNumeric: 'tabular-nums' }}
                data-testid={`ma-zahl-${feld}`}
              >
                {feld === 'gelaufen' && z && z.geplant != null
                  ? `${zaehlwert(z, 'gelaufen')} of ${zaehlwert(z, 'geplant')}`
                  : zaehlwert(z, feld)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Panel>
  )
}

function Skelett() {
  return (
    <Stack spacing={SP.tight} aria-busy="true" data-testid="ma-skelett">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: PANEL_RADIUS }} />
      ))}
    </Stack>
  )
}

export default function Monatsabschluss() {
  const [searchParams, setSearchParams] = useSearchParams()
  const monatParam = searchParams.get('monat') || ''
  const [daten, setDaten] = useState(null)
  const [laden, setLaden] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [offen, setOffen] = useState({})
  // Regel-16 H1: die zuletzt geladene Monatsliste bleibt nach einem Fehlabruf stehen,
  // damit der Umschalter nicht mit den Daten verschwindet.
  const [monateBekannt, setMonateBekannt] = useState([])

  const holen = useCallback(() => {
    let lebt = true
    setLaden(true)
    setFehler(null)
    axios.get(`${API}/api/tax/monatsabschluss`, { params: monatParam ? { monat: monatParam } : {} })
      .then((r) => {
        if (!lebt) return
        setDaten(r.data)
        if (r.data && Array.isArray(r.data.monate)) setMonateBekannt(r.data.monate)
      })
      .catch((e) => {
        if (!lebt) return
        const status = e?.response?.status
        setDaten(null)
        setFehler(status ? `Request failed (HTTP ${status}).` : (e?.message || 'Request failed.'))
      })
      .finally(() => { if (lebt) setLaden(false) })
    return () => { lebt = false }
  }, [monatParam])

  useEffect(() => holen(), [holen])

  const gruppen = useMemo(() => (daten && daten.zeile ? gruppieren(daten) : []), [daten])

  // Gruppen mit Befund oder Ausfall stehen beim Laden offen (Plan §5.4).
  useEffect(() => {
    const vorgabe = {}
    for (const g of gruppen) {
      const s = gruppenstand(g)
      vorgabe[g.nummer] = s.befund > 0 || s.gescheitert > 0
    }
    setOffen(vorgabe)
  }, [gruppen])

  const monate = useMemo(() => {
    const liste = [...((daten && daten.monate) || monateBekannt)]
    const aktuell = (daten && daten.monat) || monatParam || laufenderMonat()
    if (aktuell && !liste.includes(aktuell)) liste.unshift(aktuell)
    return liste.sort().reverse()
  }, [daten, monatParam, monateBekannt])

  const abschnitte = []
  let letzterAbschnitt = null
  for (const g of gruppen) {
    if (g.abschnitt && g.abschnitt !== letzterAbschnitt) {
      abschnitte.push(
        <Typography key={`a-${g.abschnitt}`} variant="overline" component="h2" color="text.secondary">
          {g.abschnitt}
        </Typography>,
      )
      letzterAbschnitt = g.abschnitt
    }
    abschnitte.push(
      <Gruppe
        key={g.nummer}
        gruppe={g}
        pruefungen={daten.pruefungen || {}}
        offen={!!offen[g.nummer]}
        umschalten={() => setOffen((o) => ({ ...o, [g.nummer]: !o[g.nummer] }))}
      />,
    )
  }

  const gezeigterMonat = (daten && daten.monat) || monatParam || laufenderMonat()

  return (
    <PageContainer width="wide" boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Month-end Close"
          subtitle="State of the BB month-end tasks from the daily check run — read-only."
          rightAdornment={monate.length ? (
            monate.length <= 1 ? (
              // 🔴 G13b-1 (§2.4 S1, A19): ein Auswahlfeld mit genau EINER Option
              // ist ein Bedienelement ohne Wahl — es sieht bedienbar aus und ist
              // es nicht. Die URL (`?monat=`) und der Abruf bleiben unverändert;
              // nur das Bedienelement wird zur Auskunft.
              //
              // 🔴 R16 C1 (Runde 1): der Text trug KEINE Beschriftung. Am Telefon
              // stapelt `ViewHeader` den Aktionsbereich, und „October 2026" stand
              // dort ohne Bezug unter dem Untertitel — der Select-Zweig trägt an
              // derselben Stelle „Month". Die Beschriftung ist deshalb sichtbar,
              // in der Typografie des Feldes (`caption` / `text.secondary`).
              <Box
                sx={{
                  ...CONTROL_HEIGHT_SX,
                  // Zwei Zeilen brauchen mehr als den Knopf-Boden: der Boden
                  // bleibt als Untergrenze stehen, die Höhe wächst mit dem Inhalt.
                  height: 'auto',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" data-testid="ma-monat-label">
                  Month
                </Typography>
                <Typography variant="body2" data-testid="ma-monat-text">
                  {monatsname(gezeigterMonat)}
                </Typography>
              </Box>
            ) : (
              <TextField
                select
                label="Month"
                value={gezeigterMonat}
                onChange={(e) => setSearchParams({ monat: e.target.value })}
                sx={{
                  minWidth: '12em',
                  // 🔴 F2, Kopfzeilen-Fall (2026-10-07): das Monats-Select ist
                  // eine Kopfaktion, kein Formularfeld — es traegt dieselbe Hoehe
                  // wie die Knoepfe daneben (`CONTROL_HEIGHT_SX`, 32 px Desktop).
                  '& .MuiInputBase-root': { ...CONTROL_HEIGHT_SX },
                  // Und der innere Block muss mit: er ist 23 px Zeilenbox
                  // + 2 × 8,5 px Polster = 40 px hoch, unabhaengig von der Wurzel.
                  '& .MuiSelect-select': { py: 0.5 },
                }}
                inputProps={{ 'data-testid': 'ma-monat' }}
              >
                {monate.map((m) => <MenuItem key={m} value={m}>{monatsname(m)}</MenuItem>)}
              </TextField>
            )
          ) : null}
        />
      </Box>

      {laden ? <Skelett /> : null}

      {/* 🔴 G13b-1: `liste-panel` ist die Messfläche der Proben (`oeffne` wartet
          auf sie, P2 misst ihr erstes Kind). Sie steht deshalb in JEDEM
          Nicht-Lade-Zustand — auch im Fehler- und im Leerzustand. */}
      {!laden && (fehler || daten) ? (
        <Stack spacing={SP.section} data-testid="liste-panel">
          {fehler ? (
            <Panel>
              <EmptyState
                reason="error"
                message="The month-end state could not be loaded."
                detail={`${fehler} Whether there are findings is therefore unknown (${KEIN_WERT}).`}
                action={<Button variant="outlined" onClick={holen}>Try again</Button>}
              />
            </Panel>
          ) : (
            <>
              <Hinweise daten={daten} />
              {daten.zeile ? (
                <>
                  <Kopf daten={daten} />
                  <Stack spacing={SP.tight} component="section" aria-label="The 12 groups of the BB cockpit">
                    {abschnitte}
                  </Stack>
                  <WeiterePruefungen kennungen={daten.weitere_pruefungen || []} pruefungen={daten.pruefungen || {}} />
                </>
              ) : (
                <Panel data-testid="ma-kein-ergebnis">
                  <EmptyState
                    reason="no-data"
                    /* 🔴 Der Monat bleibt im `message` (§2.4 S2/W3): der Grundsatz
                       in `detail` nennt keinen Monat, und auf einer Steuerfläche
                       darf nicht offenbleiben, WELCHER Monat gemeint ist. */
                    message={`There is no run result for ${monatsname(daten.monat)}.`}
                    detail={KEIN_ERGEBNIS_TEXT[daten.kein_ergebnis_grund] || KEIN_WERT}
                    action={(
                      <Button
                        variant="outlined"
                        onClick={daten.monat === laufenderMonat()
                          ? holen
                          : () => setSearchParams({ monat: laufenderMonat() })}
                      >
                        {daten.monat === laufenderMonat() ? 'Try again' : 'Show the current month'}
                      </Button>
                    )}
                  />
                </Panel>
              )}
            </>
          )}
        </Stack>
      ) : null}
    </PageContainer>
  )
}
